// src/oura/components/search/RankCard.tsx
//
// Where the site ranks, and its own name against what it sells.
//
// Two things about `buckets` the card must respect, because neither is
// guessable from the labels: they count NON-BRANDED searches only, and the
// bands are CUMULATIVE (top 3 is inside top 10 is inside top 20). They do not
// sum to the keyword count and are never drawn as segments of one bar.

import { TrendingUpIcon } from "lucide-react";
import type { AeoSearchPerformance, AeoSearchRollup } from "@service/aeo";
import { cn } from "../../lib/cn";
import { FoldCard, InnerStat, TYPE } from "@oura/ui";
import { TABLE } from "../client/staffScreen";
import { num, pct, searchesForWhatYouSell } from "./format";

/** Why the score reads what it reads, on hover of the head fact. */
const SCORE_TIP = "Where the site ranks for what it sells, how far that reaches, and whether those rankings earn clicks.";

export function RankCard({ search, isStaff }: { search: AeoSearchPerformance; isStaff?: boolean }) {
    const b = search.buckets;
    // Below 20 is its own count; the three above it are cumulative.
    const bands: Array<[string, number | undefined]> = [
        ["In the top 3", b?.top3],
        ["In the top 10", b?.top10],
        ["In the top 20", b?.top20],
        ["Below 20", b?.beyond20],
    ];
    const forWhatYouSell = searchesForWhatYouSell(b);
    const rows: Array<{ key: string; label: string; roll?: AeoSearchRollup; total?: boolean }> = [
        { key: "brand", label: "Your name", roll: search.branded },
        { key: "sell", label: "What you sell", roll: search.nonBranded },
        { key: "all", label: "All searches", roll: search.totals, total: true },
    ];

    return (
        <FoldCard
            id="rank"
            title="Where you rank"
            icon={{ icon: TrendingUpIcon, area: "google" }}
            // The score used to be a ring of its own. Rings are for the AI
            // engines only, so it is the one fact on this head. Null is "not
            // measured" and shows nothing, never a 0.
            right={search.searchScore !== null && (
                <span title={SCORE_TIP} className="whitespace-nowrap">
                    Search score <span className="font-medium tabular-nums text-gray-900">{search.searchScore}</span> of 100
                </span>
            )}
            foldedFact={b ? `${b.top10} in the top 10` : undefined}
        >
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {bands.map(([label, value]) => (
                    <InnerStat key={label} label={label} value={<span className={TYPE.num}>{num(value)}</span>} />
                ))}
            </div>
            <p className={cn(TYPE.tm, "m-0 mt-2 text-gray-500")}>
                {forWhatYouSell !== null && <><span className="tabular-nums">{num(forWhatYouSell)}</span> searches for what you sell, not your name. </>}
                Top 3 is counted in the top 10 too.
            </p>
            {/* The client has no hover on a phone and no idea what the score
                weighs, so it is said once, in words, under the bands. */}
            {!isStaff && search.searchScore !== null && (
                <p className={cn(TYPE.tm, "m-0 mt-1 text-gray-500")}>
                    Search score <span className="tabular-nums">{search.searchScore}</span> of 100: how high you rank for what you
                    sell, for how many of those searches, and how often they bring a visit.
                </p>
            )}

            <p className={cn(TYPE.lab, "m-0 mt-5")}>Your name against what you sell</p>
            {/* Fits a 390px phone with no sideways scroll: the rate is the
                one column that is arithmetic over the other two, so it is
                the one that steps aside below 640px. */}
            <div className={cn(TABLE.scroll, "-mx-5 mt-1")}>
                <table className="w-full table-fixed border-collapse">
                    <colgroup>
                        <col />
                        <col className="w-16 sm:w-20" />
                        <col className="w-24 sm:w-28" />
                        <col className="hidden w-24 sm:table-column" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className={cn(TABLE.th, "text-left")}>Searches for</th>
                            <th className={cn(TABLE.th, "text-right")}>Visits</th>
                            <th className={cn(TABLE.th, "text-right")}>Times shown</th>
                            <th className={cn(TABLE.th, "hidden text-right sm:table-cell")}>{isStaff ? "Click rate" : "% who visit"}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.key}>
                                <td className={cn(TABLE.td, TYPE.tb, "text-gray-900", r.total && "font-medium")}>{r.label}</td>
                                <td className={cn(TABLE.td, TYPE.tb, "text-right font-medium tabular-nums text-gray-900")}>{num(r.roll?.clicks)}</td>
                                <td className={cn(TABLE.td, TYPE.tc, "text-right tabular-nums text-gray-500")}>{num(r.roll?.impressions)}</td>
                                <td className={cn(TABLE.td, TYPE.tc, "hidden text-right tabular-nums text-gray-500 sm:table-cell")}>{pct(r.roll?.ctr)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </FoldCard>
    );
}
